import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useStudentFeesHook } from '../hooks/useStudentFeesHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import { simplifiedStudentUserListToMap } from '../../../../utils/helpers';
import { TAttendanceByDateRecord } from '../../../../apis/__shared/attendance.interfaces';
import { TStudentFeeSummary } from '../../../../apis/admissions/adminssionFees.interfaces';
import { TStudentDetailsSimplified } from '../../../../apis/students/students.interfaces';
import { useStudentsState } from './students.context';
import { useStudentFiltersState } from './studentFilters.context';

type TStudentFeesAppState = {
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  FeesTrackerList: TStudentFeeSummary[];
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STUDENT_FEES = 'UPDATE_STUDENT_FEES',
  UPDATE_FEES_TRACKER_LIST = 'UPDATE_FEES_TRACKER_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
      type: ACTION_TYPE.UPDATE_STUDENT_FEES;
      payload: TAttendanceByDateRecord[];
    }
  | {
      type: ACTION_TYPE.UPDATE_FEES_TRACKER_LIST;
      payload: TStudentFeeSummary[];
    }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialStudentFeesState: TStudentFeesAppState = {
  FeesTrackerList: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
};

type TStudentFeesState = {
  StudentFeesAppState: TStudentFeesAppState;
  studentFeesRecords: TStudentFeeSummary[];
  simplifiedStudentObject: Record<string, TStudentDetailsSimplified>;
};

const StudentFeesContext = createContext<TStudentFeesState | undefined>(
  undefined
);
const StudentFeesDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const StudentFeesReducer = (
  appState: TStudentFeesAppState,
  action: Action
): TStudentFeesAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_STUDENT_FEES:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.UPDATE_FEES_TRACKER_LIST:
      return { ...appState, FeesTrackerList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentFeesProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [StudentFeesAppState, dispatch] = useReducer(
    StudentFeesReducer,
    initialStudentFeesState
  );

  const { filteredStudents } = useStudentsState();
  const filters = useStudentFiltersState();
  const isGradeSelected = !!filters.grade && filters.grade !== 'select';

  const {
    data: StudentFeesResponse,
    isLoading: StudentFeesResponseLoading,
    isError: StudentFeesResponseLoadingError,
    isSuccess: StudentFeesResponseLoadingDataSuccess,
    error: StudentFeesResponseDataError,
  } = useStudentFeesHook(StudentFeesAppState.userParams, { enabled: isGradeSelected });

  const loadingPageData = useMemo(
    () => StudentFeesResponseLoading,
    [StudentFeesResponseLoading]
  );

  const loadingPageDataSuccess = useMemo(
    () => StudentFeesResponseLoadingDataSuccess,
    [StudentFeesResponseLoadingDataSuccess]
  );

  const loadingPageDataError = useMemo(
    () => StudentFeesResponseLoadingError,
    [StudentFeesResponseLoadingError]
  );

  useEffect(() => {
    onStateChangeHandler();
    // eslint-disable-next-line
  }, [
    StudentFeesResponseDataError,
    loadingPageDataError,
    StudentFeesResponseLoading,
    loadingPageData,
    loadingPageDataSuccess,
    StudentFeesResponseLoadingError,
  ]);

  const onStateChangeHandler = () => {
    if (loadingPageData) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (loadingPageDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (loadingPageDataError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: StudentFeesResponseLoadingError
          ? (StudentFeesResponseDataError?.response?.data.message ??
            ERROR_MESSAGE.GET_STUDENT_FEES_FAILURE)
          : '',
      });
    }
  };

  const currentState = useMemo<TStudentFeesState>(
    () => ({
      StudentFeesAppState,
      studentFeesRecords: StudentFeesResponse ?? [],
      simplifiedStudentObject: simplifiedStudentUserListToMap(filteredStudents),
      FeesTrackerList: [],
    }),
    [StudentFeesAppState, StudentFeesResponse, filteredStudents]
  );

  return (
    <StudentFeesDispatchContext.Provider value={dispatch}>
      <StudentFeesContext.Provider value={currentState}>
        {children}
      </StudentFeesContext.Provider>
    </StudentFeesDispatchContext.Provider>
  );
};

export const useStudentFeesState = () => {
  const state = useContext(StudentFeesContext);
  if (state === undefined) {
    throw new Error(
      'useStudentFeesState must be used within a StudentFeesProvider'
    );
  }
  return state;
};

export const useStudentFeesActions = () => {
  const dispatch = useContext(StudentFeesDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStudentFeesActions dispatch must be within a StudentFeesProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateFeesTrackerList = (list: TStudentFeeSummary[]) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_FEES_TRACKER_LIST,
      payload: list,
    });
  };

  return { updateSearchParams, updateFeesTrackerList };
};
